import { useEffect, useState, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Col, Container as Grid, Row, Card, Modal, Form, Table } from "react-bootstrap";
import { Button } from "../../components/core/basic/Button";
import { useDates } from "../../dates/DatesContext";
import Breadcrumb from "../../components/core/blocks/Breadcrumb";
import { DatesProvider } from "../../dates/DatesContext";
import { useFetch } from "../../hooks/api";
import moment from "moment";
import { formatCurrency, formatPercent } from "../../utils/formatNumber";
import { DASHBOARD_LINKS } from "../../utils/dashboardLinks";
import Loading from "../../components/core/blocks/Loading";
import { useAppSelector } from "../../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";
import { Accordion } from "../../components/core/basic/Accordion";
import { ButtonGroup } from "../../components/core/basic/ButtonGroup";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { Chart } from "@highcharts/react";
import { trellisPalette } from "../../components/custom/analytics/palettes";
import { HELP_TEXT } from "../../modules/pandl/constants";
import { ImportButton } from "../../components/custom/import/ImportButton";
import useQueryParams from "../../hooks/useQueryParams";
import { HoverInfo } from "../../components/core/basic/Tooltip";
import { useSimpleCan } from "../../config/casl/casl";
import { useDateFormatter } from "../../i18n/useDateFormatter";

interface CurrencyFormatProps {
  value: number | null;
}

interface ButtonBucket {
  label: string;
  value: string;
  counter?: number;
}

interface LineItem {
  label: string;
  amount: number;
  is_tax: boolean;
}

interface LineItems {
  ad_spend: LineItem[];
  misc: LineItem[];
  orders: LineItem[];
  promo: LineItem[];
  refunds: LineItem[];
  warehouse: LineItem[];
  other: LineItem[];
  total: number;
}

interface LineItemsDict {
  [key: string]: LineItems;
}

// Define Type for data
interface Data {
  line_items: LineItemsDict;
  no_landed_cost_count: number;
  report_end_date: string;
}

interface TreeMapValues {
  advertising: number;
  landed_cost: number;
  tax: number;
  fba: number;
  referral: number;
  shipping: number;
  warehouse: number;
  promo: number;
  other: number;
}

interface TransactionTableProps {
  transactions: LineItem[];
  title: string;
}

const TransactionTable = ({ transactions, title }: TransactionTableProps) => {
  const { t } = useTranslation(["pandl"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);

  const currencyFormat = ({ value }: CurrencyFormatProps): string => {
    if (value === null) {
      return "-";
    }

    return formatCurrency(value, {
      marketPlace: marketPlace.marketPlace,
      decimalPlaces: 0,
    });
  };

  return (
    <Table striped bordered hover size="sm" className="mb-3">
      <thead>
        <tr>
          <th className="text-start">{title}</th>
          <th className="text-end">{t(($) => $.pandl["Amount"])}</th>
        </tr>
      </thead>
      <tbody>
        {transactions.map((item, index) => {
          if (item.is_tax) {
            return null;
          }
          return (
            <tr key={index} style={{ borderTop: item.label === "Sub Total" ? "3px solid #555" : "1px solid #dee2e6" }}>
              <td>
                {item.label}
                <HoverInfo iconCss={{ display: "inline", paddingStart: "small", color: "grey" }}>
                  {t(HELP_TEXT[item.label as keyof typeof HELP_TEXT])}
                </HoverInfo>
              </td>
              <td className="text-end">{currencyFormat({ value: item.amount })}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const Margin = ({ total, lineItems }: { total: number; lineItems: LineItems }) => {
  if (total === 0) {
    return <span>{formatPercent(0, { addPlus: false })}</span>;
  }

  let orderPrincipal = 0;
  for (let i = 0; i < lineItems["orders"].length; i++) {
    const li = lineItems["orders"][i];
    if (li?.label === "Orders") {
      orderPrincipal += li.amount;
      break;
    }
  }
  for (let i = 0; i < lineItems["refunds"].length; i++) {
    const li = lineItems["refunds"][i];
    if (li?.label === "Orders") {
      orderPrincipal += li.amount;
      break;
    }
  }
  return (
    <span>
      {formatPercent(total / orderPrincipal, {
        addPlus: false,
      })}
    </span>
  );
};

const Dashboard = () => {
  const { t } = useTranslation(["pandl"]);
  const monthYearFmt = useDateFormatter("monthYear");
  const longDate = useDateFormatter("long");
  const lastDayThisMonth = moment().endOf("month");
  const firstDayThreeMonthsAgo = moment().subtract(3, "months").startOf("month");
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");

  const [asinQuery] = useQueryParams("asin", "");
  const [productId] = useQueryParams("product_id", "");

  const identifier = asinQuery || productId;

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [showFiguresModal, setShowFiguresModal] = useState(false);
  const [asinText, setAsinText] = useState(identifier || "");
  const [asinFilter, setASINFilter] = useState(identifier || null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const { group, start, end, preStart, preEnd } = useDates();

  const getDashboardTo = () => {
    const url = `/user/pl/report`;

    return url;
  };

  const currencyFormat = ({ value }: CurrencyFormatProps): string => {
    if (value === null) {
      return "-";
    }

    return formatCurrency(value, {
      marketPlace: marketPlace.marketPlace,
      decimalPlaces: 0,
    });
  };

  const breadCrumbItems = [
    {
      name: t(($) => $.pandl["Profit and Expenses Report"]),
      href: getDashboardTo(),
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: t(($) => $.pandl["Dashboards"]),
    },
  ];

  const filters = {
    market_place: marketPlace.marketPlace,
    channel,
    date_group: group,
    start: firstDayThreeMonthsAgo.format("YYYY-MM-DD"),
    end: lastDayThisMonth.format("YYYY-MM-DD"),
    empty: false,
    group_by: "org_id",
    order_association: asinFilter !== null,
    identifier: asinFilter,
  };

  const { data, isLoading, isFetching } = useFetch<Data>(
    ["profit_report", marketPlace.marketPlace, start, end, preStart, preEnd, group, refresh],
    `/merchandise/settlement_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: true,
    },
  );

  const buttunBuckets: ButtonBucket[] = useMemo(() => {
    if (!data || Object.keys(data["line_items"]).length === 0) {
      return [];
    }

    const monthYearKeys = Object.keys(data["line_items"]);

    const buttunBuckets: ButtonBucket[] = [];

    monthYearKeys.forEach((key) => {
      // convert key of type MM-YYYY to MMMM YYYY
      const dt = moment(key, "MM-YYYY");
      const monthYear = monthYearFmt.format(dt.toDate());

      buttunBuckets.push({
        label: monthYear,
        value: key,
        counter: undefined,
      });
    });

    return buttunBuckets;
  }, [data, monthYearFmt]);

  useEffect(() => {
    if (data && Object.keys(data["line_items"]).length > 0) {
      const keys = Object.keys(data["line_items"]);
      const lastKey = keys[keys.length - 1];
      if (lastKey === undefined) {
        return;
      }
      setSelectedMonth(lastKey);
    }
  }, [data]);

  const { barChartOptions, treeMapOptions } = useMemo(() => {
    const getBarValues = (data: LineItems): { sales: number; advertising: number; costOfSelling: number } => {
      let sales: number = 0;
      let advertising: number = 0;
      let costOfSelling: number = 0;

      const orders: LineItem[] = data.orders;
      orders.forEach((item) => {
        if (item.label === "Orders") {
          sales += item.amount;
        }
      });
      data.refunds.forEach((item) => {
        if (item.label === "Orders") {
          sales += item.amount;
        }
      });
      data.ad_spend.forEach((item) => {
        if (item.label !== "Sub Total") {
          advertising += item.amount;
        }
      });

      const keys = Object.keys(data) as (keyof LineItems)[];
      keys.forEach((key) => {
        if (key === "ad_spend" || key === "total") {
          return;
        }
        data[key].forEach((item) => {
          if (item.label === "Sub Total" || item.label === "Orders" || item.label.includes("Tax")) {
            return;
          }
          costOfSelling += item.amount;
        });
      });

      return {
        sales,
        advertising,
        costOfSelling,
      };
    };

    const getTreeMapValues = (data: LineItems): TreeMapValues => {
      const keys = Object.keys(data) as (keyof LineItems)[];

      const treeMapValues: TreeMapValues = {
        advertising: 0,
        landed_cost: 0,
        tax: 0,
        fba: 0,
        referral: 0,
        shipping: 0,
        warehouse: 0,
        promo: 0,
        other: 0,
      };

      keys.forEach((key) => {
        if (key === "total") {
          return;
        }
        for (let i = 0; i < data[key].length; i++) {
          const item: LineItem | undefined = data[key][i];
          if (item === undefined) {
            continue;
          }
          if (item.label === "Sub Total" || item.label === "Orders") {
            continue;
          }
          const label = item.label;

          if (key === "ad_spend") {
            treeMapValues.advertising += -item.amount;
          } else if (label.includes("Tax")) {
            treeMapValues["tax"] += -item.amount;
          } else if (label.toLowerCase().includes("fba")) {
            treeMapValues["fba"] += -item.amount;
          } else if (label.toLowerCase().includes("referral") || label.toLowerCase().includes("commission")) {
            treeMapValues["referral"] += -item.amount;
          } else if (label.toLowerCase().includes("shipping")) {
            treeMapValues["shipping"] += -item.amount;
          } else if (label.toLowerCase().includes("landed cost") || label.toLowerCase().includes("additional cost")) {
            treeMapValues["landed_cost"] += -item.amount;
          } else if (key === "warehouse" || label.toLowerCase().includes("restocking")) {
            treeMapValues["warehouse"] += -item.amount;
          } else if (
            key === "promo" ||
            label.toLowerCase().includes("promo") ||
            label.toLowerCase().includes("coupon")
          ) {
            treeMapValues["promo"] += -item.amount;
          } else {
            treeMapValues["other"] += -item.amount;
          }
        }
      });

      return treeMapValues;
    };

    if (!data || Object.keys(data["line_items"]).length === 0 || selectedMonth === null) {
      return {
        barChartOptions: null,
        treeMapOptions: null,
      };
    }

    const monthYears = Object.keys(data["line_items"]);

    const sales = [];
    const advertising = [];
    const costOfSelling = [];
    const profit = [];
    const treeMap: { [key: string]: TreeMapValues } = {};

    for (let i = 0; i < monthYears.length; i++) {
      const my: string | undefined = monthYears[i];
      if (my === undefined) {
        continue;
      }
      const monthData: LineItems | undefined = data.line_items[my];
      if (monthData === undefined) {
        continue;
      }
      const myRes = getBarValues(monthData);
      const tmRes = getTreeMapValues(monthData);
      treeMap[my] = tmRes;
      sales.push(myRes.sales);
      advertising.push(-myRes.advertising);
      costOfSelling.push(-myRes.costOfSelling);
      profit.push(myRes.sales + myRes.costOfSelling + myRes.advertising);
    }

    const barChartOptions = {
      chart: {
        type: "column",
        style: {
          "font-family": "Poppins",
          "font-weight": 500,
          "font-size": "15px",
        },
      },
      title: {
        text: undefined,
      },
      xAxis: {
        categories: monthYears.map((my) => monthYearFmt.format(moment(my, "MM-YYYY").toDate())),
        crosshair: true,
        accessibility: {
          description: t(($) => $.pandl["Months"]),
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: t(($) => $.pandl["($)"]),
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.1,
          borderWidth: 0,
        },
      },
      tooltip: {
        pointFormatter: function (this: Highcharts.Point): string {
          return `${this.series.name}: <b>${
            this.y !== undefined ?
              formatCurrency(this.y, {
                marketPlace: marketPlace.marketPlace,
                decimalPlaces: 0,
              })
            : "-"
          }</b><br/>`;
        },
      },
      series: [
        {
          name: t(($) => $.pandl["Sales"]),
          data: sales,
          color: trellisPalette[1],
        },
        {
          name: t(($) => $.pandl["Cost of Selling"]),
          data: costOfSelling,
          color: trellisPalette[3],
        },
        {
          name: t(($) => $.pandl["Advertising"]),
          data: advertising,
          color: trellisPalette[2],
        },
        {
          name: t(($) => $.pandl["Profit"]),
          data: profit,
          color: trellisPalette[12],
        },
      ],
    };

    const treeMapData = treeMap[selectedMonth];
    const treeMapOptions = {
      // tooltip: {
      //   pointFormatter: function () {
      //     return `<span style="color:${this.color}">\u25CF</span> ${
      //       this.series.name
      //     }: <b>${formatCurrency(this.y, marketPlace.marketPlace, true, false, false)}</b><br/>`;
      //   },
      // },
      chart: {
        type: "treemap",
        style: {
          "font-family": "Poppins",
          "font-weight": 500,
          "font-size": "15px",
        },
      },
      series: [
        {
          type: "treemap",
          layoutAlgorithm: "squarified",
          data: [
            {
              name: t(($) => $.pandl["Advertising"]),
              value: treeMapData ? treeMapData.advertising : 0,
              colorValue: 1,
              color: trellisPalette[2],
            },
            {
              name: t(($) => $.pandl["Landed Cost"]),
              value: treeMapData ? treeMapData.landed_cost : 0,
              colorValue: 1,
              color: trellisPalette[3],
            },
            {
              name: t(($) => $.pandl["Tax"]),
              value: treeMapData ? treeMapData.tax : 0,
              colorValue: 1,
              color: trellisPalette[4],
            },
            {
              name: t(($) => $.pandl["FBA"]),
              value: treeMapData ? treeMapData.fba : 0,
              colorValue: 1,
              color: trellisPalette[5],
            },
            {
              name: t(($) => $.pandl["Referral"]),
              value: treeMapData ? treeMapData.referral : 0,
              colorValue: 1,
              color: trellisPalette[6],
            },
            {
              name: t(($) => $.pandl["Shipping"]),
              value: treeMapData ? treeMapData.shipping : 0,
              colorValue: 1,
              color: trellisPalette[7],
            },
            {
              name: t(($) => $.pandl["Warehouse"]),
              value: treeMapData ? treeMapData.warehouse : 0,
              colorValue: 1,
              color: trellisPalette[8],
            },
            {
              name: t(($) => $.pandl["Promotion"]),
              value: treeMapData ? treeMapData.promo : 0,
              colorValue: 1,
              color: trellisPalette[9],
            },
            {
              name: t(($) => $.pandl["Other"]),
              value: treeMapData ? treeMapData.other : 0,
              colorValue: 1,
              color: trellisPalette[10],
            },
          ],
        },
      ],
      title: {
        text: undefined,
      },
    };

    return { barChartOptions, treeMapOptions };
  }, [data, marketPlace, selectedMonth, t, monthYearFmt]);

  if (isLoading || isFetching) {
    return <Loading fullPage={false} />;
  }

  return (
    <Grid
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Breadcrumb items={breadCrumbItems} />
      {data && data.no_landed_cost_count > 0 && userCanUpdatePricingPlans && (
        <Row style={{ width: "calc(100vw - 95px)" }}>
          <Col className="p-0 m-0" xs={12}>
            <div style={{ backgroundColor: trellisPalette[3], color: "white", padding: "1rem" }} className="fs-4 px-4">
              <Row>
                <Col className="pt-2" xs={10}>
                  {t(
                    ($) =>
                      $.pandl[
                        "There are {count} or more products that do not have landed cost data. To improve the accuracy of your profit and expenses report, please upload your landed cost data."
                      ],
                    { count: data.no_landed_cost_count },
                  )}
                </Col>
                <Col className="text-end" xs={2}>
                  <ImportButton
                    defaultType={`pricing_bulk_upload`}
                    importModalOpen={importModalOpen}
                    setImportModalOpen={setImportModalOpen}
                    title={t(($) => $.pandl["Upload Pricing Data"])}
                    style={{
                      border: 0,
                      backgroundColor: trellisPalette[2],
                      color: "white",
                    }}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      )}
      <div className="mb-4"> </div>
      {selectedMonth && data?.report_end_date && (
        <Row>
          <Col className="px-5" xs={12}>
            <Card>
              <Card.Body style={{ padding: "2rem" }}>
                <Row>
                  <Col xs={6}>
                    <ButtonGroup
                      buttons={buttunBuckets}
                      value={selectedMonth}
                      onSelect={(values) => {
                        setSelectedMonth(values);
                      }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Row>
                      <Col className="pb-3" xs={6}></Col>
                      <Col className="text-end pb-3" xs={6}>
                        <Row>
                          <Col xs={9}>
                            <Form.Control
                              type="text"
                              id="asin"
                              aria-describedby="passwordHelpBlock"
                              placeholder={t(($) => $.pandl["Enter ASIN or Parent ASIN"])}
                              defaultValue={asinText}
                              onChange={(e) => {
                                setAsinText(e.target.value);
                              }}
                            />
                          </Col>
                          <Col xs={3}>
                            <Button
                              onPress={() => {
                                setASINFilter(asinText);
                                setRefresh((prev) => prev + 1);
                              }}
                            >
                              {asinFilter && !asinText ? t(($) => $.pandl["Reset"]) : t(($) => $.pandl["Filter"])}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                      <Col className="pt-2 ps-4 text-end" xs={12}></Col>
                    </Row>
                  </Col>
                  <Col xs={6}>
                    <div
                      className="fs-4 fw-bold"
                      style={{
                        marginTop: "0rem",
                        marginBottom: "0.2rem",
                        textTransform: "uppercase",
                        color: trellisPalette[4],
                      }}
                    >
                      {t(($) => $.pandl["Profit and Expenses"])}
                    </div>
                    <small>
                      {monthYearFmt.format(firstDayThreeMonthsAgo.toDate())} -{" "}
                      {monthYearFmt.format(lastDayThisMonth.toDate())}
                    </small>
                    <div className="mt-3 pe-3">{barChartOptions && <Chart options={barChartOptions} />}</div>

                    <div
                      className="fs-4 fw-bold"
                      style={{
                        marginTop: "0rem",
                        marginBottom: "0.2rem",
                        textTransform: "uppercase",
                        color: trellisPalette[4],
                      }}
                    >
                      {t(($) => $.pandl["Expense Breakdown"])}
                    </div>
                    <small>{monthYearFmt.format(moment(selectedMonth, "MM-YYYY").toDate())}</small>
                    <div className="mt-3 pe-3">{treeMapOptions && <Chart options={treeMapOptions} />}</div>
                  </Col>
                  <Col xs={6}>
                    <Row>
                      <Col xs={6}>
                        <div
                          className="fs-4 fw-bold text-end"
                          style={{
                            marginTop: "0rem",
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                            color: trellisPalette[4],
                          }}
                        >
                          &nbsp;
                        </div>
                        {data.report_end_date && (
                          <div className="fs-5 fw-normal text-start">
                            {t(($) => $.pandl["Latest Settlement Report: {date}"], {
                              date: longDate.format(new Date(data["report_end_date"])),
                            })}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <div
                          className="fs-4 fw-bold text-end"
                          style={{
                            marginTop: "0rem",
                            marginBottom: "0.2rem",
                            textTransform: "uppercase",
                            color: trellisPalette[4],
                          }}
                        >
                          <span>
                            {t(($) => $.pandl["Profit:"])}{" "}
                            {selectedMonth &&
                              data.line_items[selectedMonth] &&
                              currencyFormat({ value: data.line_items[selectedMonth].total })}
                          </span>
                          <span className="mx-2">-</span>
                          <span>
                            {data.line_items[selectedMonth] && (
                              <Margin
                                total={data.line_items[selectedMonth]["total"]}
                                lineItems={data.line_items[selectedMonth]}
                              />
                            )}
                          </span>
                        </div>

                        <div className="fs-5 fw-normal text-end">
                          <span
                            style={{ color: trellisPalette[0], cursor: "pointer" }}
                            onClick={() => setShowFiguresModal(true)}
                          >
                            {t(($) => $.pandl["How are these figures calculated?"])}
                          </span>
                        </div>
                      </Col>
                    </Row>

                    {data.line_items[selectedMonth] && (
                      <>
                        <Accordion title={t(($) => $.pandl["Orders"])} defaultExpanded marginTop={`0.5rem`}>
                          <TransactionTable
                            transactions={data.line_items[selectedMonth].orders}
                            title={t(($) => $.pandl["Line Item"])}
                          />
                        </Accordion>
                        <Accordion title={t(($) => $.pandl["Refunds"])}>
                          <TransactionTable
                            transactions={data.line_items[selectedMonth].refunds}
                            title={t(($) => $.pandl["Line Item"])}
                          />
                        </Accordion>
                        <Accordion title={t(($) => $.pandl["Advertisement Spend"])}>
                          <TransactionTable
                            transactions={data.line_items[selectedMonth]["ad_spend"]}
                            title={t(($) => $.pandl["Ad Type"])}
                          />
                        </Accordion>
                        {!asinFilter && (
                          <Accordion title={t(($) => $.pandl["Warehouse"])}>
                            <TransactionTable
                              transactions={data.line_items[selectedMonth].warehouse}
                              title={t(($) => $.pandl["Line Item"])}
                            />
                          </Accordion>
                        )}
                        {!asinFilter && (
                          <Accordion title={t(($) => $.pandl["Promo"])}>
                            <TransactionTable
                              transactions={data.line_items[selectedMonth].promo}
                              title={t(($) => $.pandl["Line Item"])}
                            />
                          </Accordion>
                        )}

                        <Accordion title={t(($) => $.pandl["Miscellaneous"])}>
                          <TransactionTable
                            transactions={data.line_items[selectedMonth].other}
                            title={t(($) => $.pandl["Line Item"])}
                          />
                        </Accordion>
                      </>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      {selectedMonth === null && (
        <Row>
          <Col className="px-5 py-6 text-center" xs={12}>
            {asinFilter && (
              <Row>
                <Col xs={12}>
                  <span className="fs-3">
                    {t(($) => $.pandl["The ASIN or Parent ASIN you have chosen was not found."])}
                  </span>
                </Col>
                <Col className="pt-3" xs={12}>
                  <span
                    className="fs-3"
                    style={{ cursor: "pointer", color: trellisPalette[0] }}
                    onClick={() => {
                      setASINFilter(null);
                      setAsinText("");
                      setRefresh((prev) => prev + 1);
                    }}
                  >
                    {t(($) => $.pandl["Reset Filter"])}
                  </span>
                </Col>
              </Row>
            )}
            {asinFilter === null && (
              <span className="fs-3">
                {t(($) => $.pandl["The Profit and Expenses Reports have not yet been generated."])}
              </span>
            )}
          </Col>
        </Row>
      )}
      <Modal show={showFiguresModal} onHide={() => setShowFiguresModal(false)} size={"lg"}>
        <Modal.Header closeButton>
          <span className="fs-2">{t(($) => $.pandl["How are these figures calculated?"])}</span>
        </Modal.Header>
        <Modal.Body>
          <div className="p-4">
            <p>
              {t(
                ($) =>
                  $.pandl[
                    "These figures come from Amazon's bi-weekly Settlement Reports. We group the different line items from the settlement report and display them in categories."
                  ],
              )}
            </p>
            <ul className="fs-4">
              <li>
                <Trans
                  t={t}
                  i18nKey={($) =>
                    $.pandl[
                      "<strong>Order-Linked Items</strong>: Orders, Refunds, and Advertising spend are tied directly to orders"
                    ]
                  }
                  components={{ strong: <strong /> }}
                />
              </li>
              <li>
                <Trans
                  t={t}
                  i18nKey={($) =>
                    $.pandl[
                      "<strong>General Fees</strong>: Warehouse Fees, Promo, and Miscellaneous fees are only visible in monthly reports as they aren't order-specific."
                    ]
                  }
                  components={{ strong: <strong /> }}
                />
              </li>
            </ul>
            <p>{t(($) => $.pandl["For greater accuracy, we recommend uploading your landed costs."])}</p>
          </div>
        </Modal.Body>
      </Modal>
    </Grid>
  );
};

const PLDashboard = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default PLDashboard;
